import { useCallback, useMemo, useState } from 'react'

import { Heading } from '@circlefin/components'
import { useForm, y } from '@circlefin/form'
import { TransWithLink } from '@shared/components/common'
import { Center } from '@shared/components/layout'
import {
  BlockchainRecipientAddressOwner,
  useWalletProvidersQuery,
  WalletType,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

const schema = y.object({
  /**
   * Wallet type.
   */
  walletType: y.string().oneOf(Object.values(WalletType)).required(),
  /**
   * Wallet provider object.
   */
  vasp: y
    .object({
      /**
       * Wallet provider id.
       */
      id: y.string(),
      /**
       * Wallet provider name.
       */
      name: y.string(),
      /**
       * Wallet provider network.
       */
      network: y.string(),
    })
    .when('walletType', {
      is: 'HOSTED',
      then: () =>
        y.object({
          /**
           * Provider id is required for hosted wallet.
           */
          id: y.string().required(),
          /**
           * Provider name is required for hosted wallet.
           */
          name: y.string().required(),
          /**
           * Provider network is optional for hosted wallet.
           */
          network: y.string().notRequired(),
        }),
      /**
       * Provider object is not required for self hosted wallet.
       */
      otherwise: () => y.object(),
    }),
})

export type AdditionalWalletInfoFormValues = y.InferType<typeof schema>

export interface AdditionalWalletInfoFormProps {
  /**
   * On Form Submit callback.
   */
  onSubmit: (values: AdditionalWalletInfoFormValues) => void
  /**
   * Loading state of mutation.
   */
  loading: boolean
  /**
   * Address owner.
   */
  owner: BlockchainRecipientAddressOwner
}

export const AdditionalWalletInfoForm: React.FC<
  AdditionalWalletInfoFormProps
> = ({ onSubmit, loading, owner }) => {
  const { t } = useTranslation('address-book/modals')

  const [searchTerm, setSearchTerm] = useState<string>()

  const { data: walletProvidersData } = useWalletProvidersQuery()

  const walletProviderDropdownItems = useMemo(() => {
    if (!walletProvidersData) return []

    return walletProvidersData.walletProviders.map((provider) => {
      return {
        label: provider.name,
        value: {
          id: provider.id,
          name: provider.name,
          network: provider.network,
        },
      }
    })
  }, [walletProvidersData])

  const [Form, { watch, resetField }] = useForm<AdditionalWalletInfoFormValues>(
    {
      schema,
    },
  )

  // Reset wallet provider when self-hosted is selected
  const handleSelfHostedSelected = useCallback(() => {
    resetField('vasp')
  }, [resetField])

  return (
    <Center variant="horizontal">
      <Form onSubmit={onSubmit}>
        <div className="flex w-128 flex-col gap-y-6">
          <Form.SelectCard
            label="hosted"
            name="walletType"
            type="radio"
            value="HOSTED"
          >
            <Heading iconName="WalletOutline">
              <Heading.Header>
                {owner === BlockchainRecipientAddressOwner.customer
                  ? t(
                      'addAddress.additionalWalletInfo.walletType.firstParty.hosted',
                    )
                  : t(
                      'addAddress.additionalWalletInfo.walletType.thirdParty.hosted',
                    )}
              </Heading.Header>
            </Heading>
          </Form.SelectCard>
          <Form.SelectCard
            label="self hosted"
            name="walletType"
            onChange={handleSelfHostedSelected}
            type="radio"
            value="SELF_HOSTED"
          >
            <Heading iconName="KeySolid">
              <Heading.Header>
                {owner === BlockchainRecipientAddressOwner.customer
                  ? t(
                      'addAddress.additionalWalletInfo.walletType.firstParty.selfHosted',
                    )
                  : t(
                      'addAddress.additionalWalletInfo.walletType.thirdParty.selfHosted',
                    )}
              </Heading.Header>
            </Heading>
          </Form.SelectCard>
        </div>

        <div className="flex flex-col space-y-4 pt-16">
          {/* Hide wallet provider dropdown unless wallet type is hosted */}
          {watch('walletType') === WalletType.HOSTED && (
            <>
              <Center className="mb-6 pb-4" variant="horizontal">
                <h2 className="type-h-section-md">
                  {t(
                    'addAddress.additionalWalletInfo.walletProvider.selectProvider',
                  )}
                </h2>
                <h3 className="text-neutral-subtle type-body-base-bold">
                  {/* TODO: Add subtitle once copy is finalized */}
                </h3>
              </Center>
              <Form.Combobox
                className="w-full"
                items={walletProviderDropdownItems}
                label={t(
                  'addAddress.additionalWalletInfo.walletProvider.label',
                )}
                name="vasp"
                onInputChange={setSearchTerm}
                searchTerm={searchTerm}
              />
              <p className="type-body-sm">
                <TransWithLink
                  components={{ b: <b /> }}
                  i18nKey="address-book/modals:addAddress.additionalWalletInfo.walletProvider.requestProvider"
                  variant="internal-support"
                />
              </p>
            </>
          )}
          <Form.SubmitButton
            className="w-full"
            loading={loading}
            variant="primary"
          >
            {t`common:submit`}
          </Form.SubmitButton>
        </div>
      </Form>
    </Center>
  )
}
